import {
  type Airport,
  type AirportsFile,
  airportCity,
  airportFromRow,
  countryName,
  usStateCode,
} from '@trips/refdata';
import { useEffect, useState } from 'react';

/** Airports load on demand (about 200 KB gzipped) the first time a form needs them. */
let airportsPromise: Promise<Airport[]> | null = null;
let airportsList: Airport[] | null = null;
let byCode: Map<string, Airport> | null = null;

export function loadAirports(): Promise<Airport[]> {
  airportsPromise ??= import('@trips/refdata/airports.json').then((m) => {
    const file = (m.default ?? m) as unknown as AirportsFile;
    airportsList = file.rows.map(airportFromRow);
    byCode = new Map(airportsList.map((a) => [a.iata, a]));
    return airportsList;
  });
  return airportsPromise;
}

export function useAirports(): Airport[] | null {
  const [list, setList] = useState(airportsList);
  useEffect(() => {
    if (!list) void loadAirports().then(setList);
  }, [list]);
  return list;
}

export function airportByCode(code: string): Airport | undefined {
  return byCode?.get(code.toUpperCase());
}

const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Airports matching a code, city or name, best matches first. */
export function searchAirports(list: Airport[], query: string, limit = 6): Airport[] {
  const q = norm(query.trim());
  if (!q) return [];
  const scored: { a: Airport; score: number }[] = [];
  for (const a of list) {
    let score = 0;
    const code = a.iata.toLowerCase();
    if (code === q) score = 100;
    else if (code.startsWith(q)) score = 60;
    else {
      const city = norm(airportCity(a));
      const name = norm(a.name);
      if (city === q) score = 80;
      else if (city.startsWith(q)) score = 50;
      else if (name.includes(q)) score = 30;
      else if (city.includes(q)) score = 20;
    }
    if (score > 0) {
      // Prefer airports that look like real commercial airports.
      if (/International/.test(a.name)) score += 5;
      scored.push({ a, score });
    }
  }
  return scored
    .sort((x, y) => y.score - x.score || x.a.iata.localeCompare(y.a.iata))
    .slice(0, limit)
    .map((x) => x.a);
}

export function airportRegionCode(a: Airport): string | null {
  return a.country === 'US' ? usStateCode(a.region) : null;
}

export { airportCity, countryName };
